/**
 * Class recipes for Creator Studio's Resend-style primitives (FOR-286), copied
 * from justingluska/resend-styles patterns/01–12. Components live in ui.tsx.
 */
import type { StatusTone } from "@/lib/studio"
import { cn } from "@/lib/utils"

export const rs = {
  /** text-default: --gray-11. Descriptions, labels, table headers. */
  text: "text-[#333] dark:text-[#a1a4a5]",
  /** Card: 24px radius, --gray-3 border, no fill, no shadow. */
  card: "rounded-3xl border border-[#d7d7d7] dark:border-[#212629]",
  /** Hairline row rule: --gray-2. */
  rule: "border-[#eee] dark:border-[#191b1e]",
  /** Page title (02-app-shell). */
  h1: "text-[28px] font-medium leading-[34px] tracking-[-0.045rem] text-foreground",
  /** Card heading proper (07): 20px bold. */
  heading: "text-xl font-bold tracking-[-0.16px] text-foreground",
  /** Card sub-heading (07): 16px medium. */
  subheading: "text-base font-medium text-foreground",
  /** Meta-grid / stat label (07): 12px uppercase --gray-11. */
  label: "text-xs uppercase text-[#333] dark:text-[#a1a4a5]",
  /** Stat value (07): 35px, weight 400, -0.64px. */
  stat: "text-[35px] font-normal leading-[42px] tracking-[-0.64px] tabular-nums text-foreground",
  /** Interactive fill + border (gray-a2 / gray-a3). */
  interactive:
    "border border-black/[0.15] bg-black/[0.08] dark:border-[#b0c7d925] dark:bg-[#18191ce0]",
  interactiveHover: "hover:bg-black/[0.15] dark:hover:bg-[#b0c7d925]",
  /** Input / textarea / select box (05). 16px under sm to stop iOS zoom. */
  input:
    "rounded-xl border-black/[0.15] bg-black/[0.08] text-base text-foreground shadow-none disabled:opacity-70 placeholder:text-[#989898] focus-visible:ring-2 focus-visible:ring-black/[0.08] sm:text-sm dark:border-[#b0c7d925] dark:bg-[#18191ce0] dark:placeholder:text-[#52595b] dark:focus-visible:ring-[#18191ce0]",
  /** Table header capsule cell (03). */
  th: "h-8 whitespace-nowrap border-b border-t border-black/[0.15] bg-black/[0.08] px-3 text-left text-xs font-semibold text-[#333] first:rounded-l-xl first:border-l last:rounded-r-xl last:border-r dark:border-[#b0c7d925] dark:bg-[#18191ce0] dark:text-[#a1a4a5]",
  /** Table body cell (03). */
  td: "h-10 border-b border-[#eee] px-3 py-3 text-sm dark:border-[#191b1e]",
  /** Dashed-underline primary link (03). */
  link: "cursor-pointer border-b border-dashed border-[#d7d7d7] transition duration-300 ease-in-out hover:border-[#989898] dark:border-[#212629] dark:hover:border-[#6e7679]",
  /** Floating panels: dropdowns and popovers (12). */
  panel: "rounded-2xl border-black/[0.15] bg-background shadow-lg dark:border-[#b0c7d925]",
  /** Tooltip (12). */
  tooltip:
    "max-w-xs rounded-xl border border-black/[0.15] bg-background px-3 py-2 text-sm font-normal text-foreground shadow-lg dark:border-[#b0c7d925]",
  /** The progress flow's dotted band (09): 8px grid, 2px dot. */
  dots:
    "bg-[radial-gradient(circle_at_4px_4px,#d7d7d7df_1px,transparent_1px)] bg-[size:8px_8px] dark:bg-[radial-gradient(circle_at_4px_4px,#292929df_1px,transparent_1px)]",
  /** Skeleton block. */
  pulse: "animate-pulse rounded-lg bg-black/[0.05] dark:bg-white/[0.06]",
}

export type Tone = StatusTone

/** Badge tones (04): a3 fill + a11 text, Radix dark steps. */
export const TONE: Record<Tone, string> = {
  success: "bg-[#00a43319] text-[#00713fde] dark:bg-[#22ff991e] dark:text-[#46fea5d4]",
  info: "bg-[#008ff519] text-[#006dcbf2] dark:bg-[#0077ff3a] dark:text-[#70b8ff]",
  warning: "bg-[#ffde003d] text-[#ab6400] dark:bg-[#fa820022] dark:text-[#ffca16]",
  error: "bg-[#f3000d14] text-[#c40006d3] dark:bg-[#ff173f2d] dark:text-[#ff9592]",
  violet: "bg-[#4400ee0f] text-[#1f0099af] dark:bg-[#8354fe36] dark:text-[#baa7ff]",
  neutral: "bg-[#eee] text-[#333] dark:bg-[#191b1e] dark:text-[#a1a4a5]",
}

/** a11 text alone, for icons and inline status words. */
export const TONE_TEXT: Record<Tone, string> = {
  success: "text-[#00713fde] dark:text-[#46fea5d4]",
  info: "text-[#006dcbf2] dark:text-[#70b8ff]",
  warning: "text-[#ab6400] dark:text-[#ffca16]",
  error: "text-[#c40006d3] dark:text-[#ff9592]",
  violet: "text-[#1f0099af] dark:text-[#baa7ff]",
  neutral: "text-[#333] dark:text-[#a1a4a5]",
}

/** Tab / segment item classes (06): filled pill when active, no track. */
export function tabClass(active: boolean) {
  return cn(
    "inline-flex h-8 shrink-0 cursor-pointer select-none items-center whitespace-nowrap rounded-xl px-3 text-sm font-semibold transition-colors",
    "hover:bg-black/[0.15] dark:hover:bg-[#b0c7d925]",
    active ? "bg-black/[0.08] text-[#191919] dark:bg-[#18191ce0] dark:text-white" : "text-[#333] hover:text-[#191919] dark:text-[#a1a4a5] dark:hover:text-white",
  )
}

